import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiShoppingBag } from 'react-icons/fi';
import styled from 'styled-components';

const Nav = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: ${props => props.$scrolled ? '0.8rem 10%' : '1rem 10%'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 100;
  transition: all 0.3s ease;
  background: ${props => props.$scrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent'};
  backdrop-filter: ${props => props.$scrolled ? 'blur(10px)' : 'none'};
  box-shadow: ${props => props.$scrolled ? '0 5px 20px rgba(0, 0, 0, 0.05)' : 'none'};

  @media (max-width: 768px) {
    padding: ${props => props.$scrolled ? '0.8rem 5%' : '1rem 5%'};
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
`;

const Logo = styled.img`
  height: 40px;
  width: auto;
`;

const LogoText = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #d395ff, #ff7fd9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 3rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  background: linear-gradient(135deg, #b450fc, #fc4fc8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 500;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: opacity 0.3s ease;
  position: relative;
  opacity: ${props => props.$isActive ? 1 : 0.8};

  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: ${props => props.$isActive ? '100%' : '0'};
    height: 2px;
    background: linear-gradient(135deg, #b650ff, #fa20b9);
    transition: width 0.3s ease;
  }

  &:hover {
    opacity: 1;
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const CartIcon = styled.div`
  position: relative;
  background: linear-gradient(135deg, #d395ff, #ff7fd9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  cursor: pointer;
  transition: opacity 0.3s ease;
  opacity: 0.9;

  &:hover {
    opacity: 1;
  }
`;

const CartCount = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #d395ff, #ff7fd9);
  color: white;
  font-size: 0.7rem;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-text-fill-color: white;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  background: linear-gradient(135deg, #d395ff, #ff7fd9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 300px;
  background: white; /* Solid white background - never changes */
  padding: 2rem;
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.1);
  z-index: 1000;
`;

const MobileNavLink = styled(Link)`
  display: block;
  padding: 1rem 0;
  font-size: 1.1rem;
  background: linear-gradient(135deg, #d395ff, #ff7fd9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-decoration: none;
  border-bottom: 1px solid #f0f0f0;
  font-weight: ${props => props.$isActive ? '700' : '500'};
  opacity: ${props => props.$isActive ? 1 : 0.8};
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  background: linear-gradient(135deg, #d395ff, #ff7fd9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <>
      <Nav $scrolled={scrolled}>
        <LogoContainer to="/">
          <Logo src="/assets/kemlo-logo.png" alt="Kemlo" />
          <LogoText></LogoText>
        </LogoContainer>
        
        <NavLinks>
          <NavLink 
            to="/" 
            $isActive={location.pathname === '/'}
          >
            Home
          </NavLink>
          <NavLink 
            to="/about" 
            $isActive={location.pathname === '/about'}
          >
            About
          </NavLink>
          <NavLink 
            to="/catalog" 
            $isActive={location.pathname === '/catalog'}
          >
            Catalog
          </NavLink>
          <NavLink 
            to="/contact" 
            $isActive={location.pathname === '/contact'}
          >
            Contact
          </NavLink>
        </NavLinks>

        <NavActions>
          
          <MobileMenuButton onClick={() => setIsOpen(true)}>
            <FiMenu />
          </MobileMenuButton>
        </NavActions>
      </Nav>
      
      <AnimatePresence>
        {isOpen && (
          <MobileMenu
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
          >
            <CloseButton onClick={() => setIsOpen(false)}>
              <FiX />
            </CloseButton>
            <MobileNavLink 
              to="/" 
              $isActive={location.pathname === '/'}
              onClick={() => setIsOpen(false)}
            >
              Home
            </MobileNavLink>
            <MobileNavLink 
              to="/about" 
              $isActive={location.pathname === '/about'}
              onClick={() => setIsOpen(false)}
            >
              About
            </MobileNavLink>
            <MobileNavLink 
              to="/catalog" 
              $isActive={location.pathname === '/catalog'}
              onClick={() => setIsOpen(false)}
            >
              Catalog
            </MobileNavLink>
            <MobileNavLink 
              to="/contact" 
              $isActive={location.pathname === '/contact'}
              onClick={() => setIsOpen(false)}
            >
              Contact
            </MobileNavLink>
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;